'use client';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { PopImage } from '@ps/ui/atoms/pop-image';
import { useLogin } from '@ps/ui/contexts/auth';
import { UserRound } from 'lucide-react';

interface Props {
  bioContext: BioContext;
  creatorName: string;
  creatorAvatar?: string;
}

const AVATAR_SIZE = 72;

/**
 * The `/chat` login gate for a signed-out visitor who opened the chat directly.
 * Routes through the app-level `/login?next=<chat>` entry point (the intercepted
 * login modal via `useLogin`), handing the creator's `CreatorInfo` to it as brand
 * chrome (so a new community member auto-follows this creator and the footer links
 * their privacy policy).
 */
export function NeedsLoginView({
  bioContext,
  creatorName,
  creatorAvatar,
}: Props) {
  const login = useLogin();
  const chatPath = bioUrl(bioContext).chat('non-intercepting');

  const onLogin = () => {
    login({
      creatorInfo: {
        id: bioContext.creatorId,
        urlCode: bioContext.urlCode,
        name: creatorName,
        avatar: creatorAvatar,
      },
      next: chatPath,
    });
  };

  return (
    <div className="flex w-full flex-col items-center gap-4 text-center text-white">
      <div className="h-[72px] w-[72px] overflow-hidden rounded-full ring-2 ring-white/20">
        {creatorAvatar ? (
          <PopImage
            src={creatorAvatar}
            alt={creatorName}
            width={AVATAR_SIZE}
            height={AVATAR_SIZE}
            resizing={{ width: 144, height: 144, fit: 'cover' }}
            style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-white/10">
            <UserRound className="h-8 w-8 text-white/70" aria-hidden />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-lg font-bold">Message {creatorName}</p>
        <p className="text-sm text-white/70">
          Log in to send {creatorName} a direct message.
        </p>
      </div>

      <button
        type="button"
        onClick={onLogin}
        className="w-full max-w-[280px] rounded-full bg-white px-4 py-3 text-sm font-semibold text-black transition hover:bg-white/90"
      >
        Log in
      </button>
    </div>
  );
}
